/**
 * AI-copilot driver bridge (M3).
 *
 * A tiny framework-agnostic singleton that lets the `config-editor` frontend plugin publish an
 * imperative edit handle + the current selection, and lets the separate `ai-assistant` frontend
 * plugin consume them — WITHOUT either plugin importing the other's React tree. Both plugins already
 * depend on `config-editor-common`, so this shared module is the one-way coupling point the M3 design
 * calls for: config-editor writes, ai-assistant reads. When config-editor is not mounted the handle is
 * null and the driver treats every imperative op as a no-op (the copilot degrades gracefully).
 *
 * This deliberately avoids React context: a cross-plugin React context only works if both sides load
 * the exact same module instance, which link:-workspaces usually but not always guarantee. A plain
 * subscribable ref is robust regardless of how the bundler dedupes.
 */

import { EDITABLE_FIELDS } from './fieldValidators';
import {
  DEFAULT_ARCHITECTURE,
  DEFAULT_MEMORY,
  DEFAULT_RUNTIME,
  DEFAULT_SNAPSTART_ENABLED,
  DEFAULT_TIMEOUT,
} from './normalizeApiConfig';

/** The result of an imperative field set. */
export interface SetFieldResult {
  ok: boolean;
  error?: string;
}

/** Which lambda group a new lambda goes into. */
export type LambdaGroupKind = 'endpoint' | 'authorizer';

/** The result of adding a new lambda. */
export interface AddLambdaResult {
  ok: boolean;
  error?: string;
}

/** What the config-editor knows about the config it currently has loaded. */
export interface ConfigEditorSelection {
  /** 'api' | 'step-function' */
  type: string;
  component: string;
  item: string;
  branch?: string;
}

/**
 * The imperative surface the config-editor exposes to the AI driver. `setLambdaField` writes through
 * the editor's own `editedConfig` state (so the existing dirty-diff + Save wiring lights up), rather
 * than simulating DOM input. Expand/highlight/focus-Save are handled by the driver via data-ai-anchor
 * DOM nodes, so they are intentionally NOT part of this handle.
 */
export interface ConfigEditorEditHandle {
  /** Resolved template name, e.g. 'phx-api-v2' | 'condor-api-v1' | 'valkyrie-api-v1'. */
  templateName: string;
  /** True once the formatted baseline is loaded and the visual editor is rendered. */
  ready: boolean;
  /** Endpoint-lambda names currently in the loaded config (the AI-editable set). */
  lambdaNames: string[];
  /**
   * Set one editable field on one endpoint lambda. Returns {ok:false} for unknown fields or unknown
   * lambda names. `value` is trusted to be already validated by the backend planner
   * (validateFieldValue) — env_vars arrives as a Record and is merged into the lambda's existing map.
   */
  setLambdaField: (name: string, field: string, value: unknown) => SetFieldResult;
  /**
   * Add a NEW lambda to the given group with sensible defaults (the AI then fills its fields via
   * setLambdaField). Returns {ok:false} for a blank/duplicate name or an unsupported template.
   * Optional: a handle registered before create-lambda support won't have it, and the driver treats
   * its absence as "adding lambdas isn't supported here".
   */
  addLambda?: (group: LambdaGroupKind, name: string) => AddLambdaResult;
}

interface BridgeState {
  selection: ConfigEditorSelection | null;
  edit: ConfigEditorEditHandle | null;
}

const state: BridgeState = { selection: null, edit: null };
const subscribers = new Set<() => void>();

function notify(): void {
  // Copy to tolerate unsubscribe-during-notify.
  for (const cb of Array.from(subscribers)) {
    try {
      cb();
    } catch {
      // A misbehaving subscriber must not break the others.
    }
  }
}

/** config-editor: publish (or clear, with null) the current selection. */
export function publishConfigEditorSelection(selection: ConfigEditorSelection | null): void {
  state.selection = selection;
  notify();
}

/** config-editor: register (or clear, with null) the imperative edit handle. */
export function registerConfigEditorEditHandle(handle: ConfigEditorEditHandle | null): void {
  state.edit = handle;
  notify();
}

/** ai-assistant: read the current bridge state (selection + edit handle). */
export function getConfigEditorBridgeState(): Readonly<BridgeState> {
  return state;
}

/** ai-assistant: subscribe to any bridge change. Returns an unsubscribe fn. */
export function subscribeConfigEditorBridge(cb: () => void): () => void {
  subscribers.add(cb);
  return () => {
    subscribers.delete(cb);
  };
}

/** Guard used by both the handle and the driver: is `field` an editable field? */
export function isEditableField(field: string): boolean {
  return (EDITABLE_FIELDS as readonly string[]).includes(field);
}

/** The lambda groups the AI copilot may edit. All three templates use endpoint_lambdas; phx also
 * has authorizer_lambdas (the M2 planner can surface either). */
const EDITABLE_LAMBDA_GROUPS = ['endpoint_lambdas', 'authorizer_lambdas'];

/** Names of every AI-editable lambda in a config (endpoint + authorizer). */
export function editableLambdaNames(config: any): string[] {
  const names: string[] = [];
  for (const group of EDITABLE_LAMBDA_GROUPS) {
    const lambdas = config?.[group]?.lambdas;
    if (lambdas) names.push(...Object.keys(lambdas));
  }
  return names;
}

export interface LambdaFieldPatchResult {
  ok: boolean;
  /** A NEW config object with the field applied (only when ok). */
  config?: any;
  error?: string;
}

/**
 * Immutably set one editable field on one lambda, resolving the lambda in whichever group
 * (endpoint_lambdas / authorizer_lambdas) actually contains it. Pure — returns a new config and never
 * mutates the input. `env_vars` merges into the lambda's existing map (the value is a single-entry
 * Record from the backend's validateFieldValue); `snapstart` mirrors the UI's compound write by also
 * forcing snap_alias on when enabled. Unknown field or unknown lambda -> {ok:false}.
 */
export function applyLambdaFieldPatch(
  config: any,
  name: string,
  field: string,
  value: unknown,
): LambdaFieldPatchResult {
  if (!isEditableField(field)) {
    return { ok: false, error: `unknown field "${field}"` };
  }
  const group = EDITABLE_LAMBDA_GROUPS.find(g => config?.[g]?.lambdas?.[name]);
  if (!group) {
    return { ok: false, error: `unknown lambda "${name}"` };
  }
  const current = config[group].lambdas[name];
  let patch: Record<string, unknown>;
  if (field === 'snapstart') {
    patch = value ? { snapstart: true, snap_alias: true } : { snapstart: false };
  } else if (field === 'env_vars') {
    patch = {
      env_vars: {
        ...(current.env_vars ?? {}),
        ...(value as Record<string, string>),
      },
    };
  } else {
    patch = { [field]: value };
  }
  return {
    ok: true,
    config: {
      ...config,
      [group]: {
        ...config[group],
        lambdas: {
          ...config[group].lambdas,
          [name]: { ...current, ...patch },
        },
      },
    },
  };
}

export interface AddLambdaPatchResult {
  ok: boolean;
  /** A NEW config object with the lambda added (only when ok). */
  config?: any;
  error?: string;
}

/**
 * Immutably add a new lambda to endpoint_lambdas / authorizer_lambdas with the same defaults the
 * Visual Editor's "Add Lambda" button uses (empty handler/source, java21/arm64, 2048 MB, 60 s,
 * snapstart per group). Pure — returns a new config. The AI then fills the lambda's fields via
 * setLambdaField. Rejects a blank name or a name already used in either group.
 */
export function applyAddLambda(
  config: any,
  group: LambdaGroupKind,
  name: string,
): AddLambdaPatchResult {
  const trimmed = String(name ?? '').trim();
  if (!trimmed) {
    return { ok: false, error: 'a new lambda needs a non-empty name' };
  }
  // Names must be unique across BOTH groups (the editor keys lambdas by name).
  if (EDITABLE_LAMBDA_GROUPS.some(g => config?.[g]?.lambdas?.[trimmed])) {
    return { ok: false, error: `lambda "${trimmed}" already exists` };
  }
  const groupKey = group === 'authorizer' ? 'authorizer_lambdas' : 'endpoint_lambdas';
  const isAuthorizer = groupKey === 'authorizer_lambdas';
  const newLambda = {
    lambda_handler: '',
    lambda_source: '',
    architecture: DEFAULT_ARCHITECTURE,
    runtime: DEFAULT_RUNTIME,
    memory_size: DEFAULT_MEMORY,
    timeout: DEFAULT_TIMEOUT,
    // Authorizers default snapstart on (matches the editor); endpoints follow the global default.
    snapstart: isAuthorizer ? true : DEFAULT_SNAPSTART_ENABLED,
    iam_policy_names: [],
    iam_policy_names_custom: [],
  };
  const existingGroup = config?.[groupKey] ?? {};
  return {
    ok: true,
    config: {
      ...config,
      [groupKey]: {
        ...existingGroup,
        lambdas: { ...(existingGroup.lambdas ?? {}), [trimmed]: newLambda },
      },
    },
  };
}
